import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
import { usePostHog } from '#app/hooks/usePostHog.js';
import { logger } from '#app/services/logger.js';
import { sessionStorage } from '#app/services/storage.js';
import { parseJson } from '#app/utilities/parseJson.js';
import {
  type Experiment,
  type FeatureFlags,
  getDefaultFeatureFlags,
} from '@contra/feature-flags';
import { safeStringify } from '@contra/utilities/safeStringify';
import { MutableRefObject } from 'react';

const log = logger.child({
  namespace: 'useFeatureFlag',
});

const isFeatureFlag = (flag: string): flag is keyof FeatureFlags => {
  return Object.prototype.hasOwnProperty.call(getDefaultFeatureFlags(), flag);
};

const testFeatureFlagFallbackValues = {
  ...getDefaultFeatureFlags(),
} as FeatureFlags;

export type ExtractExperimentKeys<T> = {
  [J in keyof T]: T[J] extends Experiment ? J : never;
}[keyof T];

const isExperimentKey = (
  key: string,
): key is ExtractExperimentKeys<FeatureFlags> => {
  const flags = getDefaultFeatureFlags();
  const experimentKeys: string[] = [];

  for (const [flagKey, value] of Object.entries(flags)) {
    if (typeof value === 'string') {
      experimentKeys.push(flagKey);
    }
  }

  return experimentKeys.includes(key);
};

const warn = (
  warnings: MutableRefObject<string[]> | undefined,
  message: string,
) => {
  if (!warnings?.current.includes(message)) {
    log.warn(message);

    warnings?.current.push(message);
  }
};

const getExplicitFeatureFlag = (
  name: string,
  localStorageFlags: FeatureFlags,
  warnings?: MutableRefObject<string[]>,
) => {
  const localStorageFlag = localStorageFlags[name];

  if (localStorageFlag !== undefined) {
    return localStorageFlag;
  }

  warn(warnings, 'accessed unset feature flag "' + name + '"');

  const testFeatureFlagFallbackValue = testFeatureFlagFallbackValues[name];

  if (testFeatureFlagFallbackValue !== undefined) {
    warn(
      warnings,
      `using hardcoded fallback value ${name}: ${safeStringify(
        testFeatureFlagFallbackValue,
      )}`,
    );

    return testFeatureFlagFallbackValue;
  }

  throw new Error('Accessed unknown feature flag "' + name + '"');
};

const overrideFeatureFlags = (
  warnings: MutableRefObject<string[]> | undefined,
  flags: FeatureFlags,
  localStorageFlags: FeatureFlags,
) => {
  const overrides: Partial<FeatureFlags> = {};

  for (const [localStorageFlagName, localStorageFlagValue] of Object.entries(
    localStorageFlags,
  )) {
    if (isFeatureFlag(localStorageFlagName)) {
      if (flags[localStorageFlagName] !== localStorageFlagValue) {
        if (isExperimentKey(localStorageFlagName)) {
          overrides[localStorageFlagName] = localStorageFlagValue as Experiment;
        } else if (typeof localStorageFlagValue === 'boolean') {
          overrides[localStorageFlagName] = localStorageFlagValue;
        } else {
          overrides[localStorageFlagName] = false;
        }

        log.info(
          '"%s" value was overridden to "%s" using CONTRA_SESSION_OVERRIDE_FEATURE_FLAGS',
          localStorageFlagName,
          localStorageFlagValue,
        );
      }
    } else {
      warn(
        warnings,
        `invalid attempt to override an unknown feature flag "${localStorageFlagName}" using CONTRA_SESSION_OVERRIDE_FEATURE_FLAGS`,
      );
    }
  }

  return overrides;
};

/**
 * Use to test against boolean feature flags ONLY.
 * For experiments, use the `useExperiment` hook that returns `true`
 * if a feature flag returns the 'test' value.
 */
export const useFeatureFlag = <
  K extends keyof FeatureFlags = keyof FeatureFlags,
>(
  name: K,
): FeatureFlags[K] => {
  const { explicitFeatureFlags, playwrightTest } = useAppConfiguration();

  const { featureFlags, warnings } = usePostHog();

  const localStorageFlags: FeatureFlags = parseJson(
    sessionStorage.getItem('CONTRA_SESSION_OVERRIDE_FEATURE_FLAGS') ?? '{}',
  );

  if (playwrightTest) {
    if (explicitFeatureFlags) {
      return getExplicitFeatureFlag(name, localStorageFlags, warnings);
    }

    throw new Error('Playwright tests must use explicit feature flags');
  }

  const defaultFeatureFlags = getDefaultFeatureFlags();

  const flags: FeatureFlags = {
    ...defaultFeatureFlags,
    ...featureFlags,
  };

  const flagsWithOverrides = {
    ...flags,
    ...overrideFeatureFlags(warnings, flags, localStorageFlags),
  };

  const flag = flagsWithOverrides[name];

  if (flag === undefined) {
    throw new Error('Unknown feature flag "' + name + '"');
  }

  return flag ?? false;
};
